import { mm, type Um } from './units.js';
import type { HardwareItem, Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, DrawerSet, FrontRect, ShelfSet } from './document.js';
import { makePanel } from './geometry.js';
import { panelSize } from './assembly.js';

/**
 * Drawer sets ("Ladice") of free-panel assemblies: `count` drawers stacked in one opening between
 * two side panels (the slides need them) and a bottom / top panel. The opening height is split into
 * equal slots; every drawer box is a frame of equal height — two long sides outside, two short
 * pieces between them — standing on a bottom panel. Boxes are generated on every evaluation.
 */

export const MAX_DRAWERS = 12;
export const DEFAULT_SLIDE_CLEARANCE = mm(13);
export const DEFAULT_DRAWER_TOP_CLEARANCE = mm(10);
export const DEFAULT_DRAWER_BOTTOM_CLEARANCE = mm(10);
export const DEFAULT_DRAWER_REAR_CLEARANCE = mm(10);

interface Box {
  min: Vec3;
  max: Vec3;
}
const box = (p: AssemblyPanel): Box => {
  const s = panelSize(p);
  return { min: [...p.position], max: [p.position[0] + s[0], p.position[1] + s[1], p.position[2] + s[2]] };
};

/** Opening of a drawer set (local µm), or null when a bounding panel is gone. */
export function drawerRegion(inst: AssemblyInstance, set: DrawerSet): { x0: Um; x1: Um; y0: Um; y1: Um; z0: Um; z1: Um } | null {
  const get = (k: string) => inst.panels.find((p) => p.key === k);
  const [l, r, b, t] = [get(set.leftKey), get(set.rightKey), get(set.bottomKey), get(set.topKey)];
  if (!l || !r || !b || !t) return null;
  const B = box(b);
  return { x0: box(l).max[0], x1: box(r).min[0], y0: B.max[1], y1: box(t).min[1], z0: B.min[2], z1: B.max[2] };
}

/** Front-view slot of drawer `index` (1 = top). */
export function drawerSlotRect(inst: AssemblyInstance, set: DrawerSet, index: number): FrontRect | null {
  const r = drawerRegion(inst, set);
  if (!r) return null;
  // With dividers between the drawers every slot is shorter by its share of the divider boards.
  const d = set.dividerThickness ?? 0;
  const h = (r.y1 - r.y0 - (set.count - 1) * d) / set.count;
  const y1 = Math.round(r.y1 - (index - 1) * (h + d));
  return { x0: r.x0, x1: r.x1, y0: Math.round(y1 - h), y1 };
}

export function nextDrawerSetId(inst: AssemblyInstance): string {
  const used = new Set([...inst.panels.map((p) => p.key), ...(inst.shelfSets ?? []).map((s) => s.id), ...(inst.fronts ?? []).map((f) => f.id), ...(inst.drawerSets ?? []).map((d) => d.id)]);
  let n = (inst.drawerSets ?? []).length + 1;
  while (used.has(`ladice-${n}`)) n++;
  return `ladice-${n}`;
}

/** Why drawers cannot go into an opening bounded by these panels (null = they can). */
export function drawerOpeningError(inst: AssemblyInstance, ref: { left: string | null; right: string | null; bottom: string | null; top: string | null }): string | null {
  const isPanel = (k: string | null, o: AssemblyPanel['orientation']) => !!k && inst.panels.some((p) => p.key === k && p.orientation === o);
  if (!isPanel(ref.left, 'vertical-side') || !isPanel(ref.right, 'vertical-side')) return 'Ladice trebaju bočne stranice s obje strane (za klizače).';
  if (!isPanel(ref.bottom, 'horizontal') || !isPanel(ref.top, 'horizontal')) return 'Prostor za ladice mora imati ploču ispod i iznad.';
  if ((inst.drawerSets ?? []).some((d) => d.leftKey === ref.left && d.rightKey === ref.right && d.bottomKey === ref.bottom && d.topKey === ref.top)) return 'U ovom prostoru već su ladice.';
  return null;
}

export function makeDrawerSet(inst: AssemblyInstance, ref: { left: string | null; right: string | null; bottom: string | null; top: string | null }, count = 3): DrawerSet {
  const n = (inst.drawerSets ?? []).length + 1;
  return {
    id: nextDrawerSetId(inst),
    label: n === 1 ? 'Ladice' : `Ladice ${n}`,
    leftKey: ref.left!,
    rightKey: ref.right!,
    bottomKey: ref.bottom!,
    topKey: ref.top!,
    count,
    slideClearance: DEFAULT_SLIDE_CLEARANCE,
    topClearance: DEFAULT_DRAWER_TOP_CLEARANCE,
    bottomClearance: DEFAULT_DRAWER_BOTTOM_CLEARANCE,
    rearClearance: DEFAULT_DRAWER_REAR_CLEARANCE,
    boxThickness: mm(16),
    bottomThickness: mm(3),
    bottomSlot: 'drawer-bottom',
  };
}

export function buildDrawerSet(inst: AssemblyInstance, set: DrawerSet): { parts: PanelPart[]; hardware: HardwareItem[]; issues: Issue[] } {
  const issues: Issue[] = [];
  const say = (message: string, severity: 'warning' | 'error' = 'error') => issues.push({ severity, code: 'drawers', message: `${set.label}: ${message}`, cabinetId: inst.id });
  const r = drawerRegion(inst, set);
  if (!r) {
    say('ploča oko prostora više ne postoji — ladice se ne prave.', 'warning');
    return { parts: [], hardware: [], issues };
  }
  const t = set.boxThickness;
  const bt = set.bottomThickness;
  const parts: PanelPart[] = [];
  const hardware: HardwareItem[] = [];
  for (let k = 1; k <= set.count; k++) {
    const slot = drawerSlotRect(inst, set, k)!;
    const x0 = r.x0 + set.slideClearance;
    const x1 = r.x1 - set.slideClearance;
    const y0 = slot.y0 + set.bottomClearance;
    const y1 = slot.y1 - set.topClearance;
    const z1 = r.z1 - (set.setbacks?.[String(k)] ?? 0);
    const z0 = r.z0 + (set.rearClearance ?? DEFAULT_DRAWER_REAR_CLEARANCE);
    const w = x1 - x0;
    const fh = y1 - y0 - bt;
    const d = z1 - z0;
    if (w <= 2 * t + mm(20) || fh <= mm(30) || d <= 2 * t + mm(50)) {
      say(`ladica ${k} ne stane u prostor (širina ${(w / 1000).toFixed(1)}, visina ${((y1 - y0) / 1000).toFixed(1)}, dubina ${(d / 1000).toFixed(1)} mm).`);
      continue;
    }
    const key = (s: string) => `${set.id}-${k}-${s}`;
    const label = (s: string) => `Ladica ${k} ${s}`;
    const fy = y0 + bt;
    parts.push(
      makePanel({ cabinetId: inst.id, localKey: key('pod'), role: 'drawer-bottom', label: label('pod'), box: { min: [x0, y0, z0], size: [w, bt, d] }, lengthAxis: 'z', widthAxis: 'x', materialSlot: set.bottomSlot, grain: 'none', movable: 'slide' }),
      makePanel({ cabinetId: inst.id, localKey: key('bl'), role: 'drawer-side', label: label('bok L'), box: { min: [x0, fy, z0], size: [t, fh, d] }, lengthAxis: 'z', widthAxis: 'y', materialSlot: 'drawer-box', grain: 'length', movable: 'slide' }),
      makePanel({ cabinetId: inst.id, localKey: key('bd'), role: 'drawer-side', label: label('bok D'), box: { min: [x1 - t, fy, z0], size: [t, fh, d] }, lengthAxis: 'z', widthAxis: 'y', materialSlot: 'drawer-box', grain: 'length', movable: 'slide' }),
      makePanel({ cabinetId: inst.id, localKey: key('z'), role: 'drawer-back', label: label('zadnja'), box: { min: [x0 + t, fy, z0], size: [w - 2 * t, fh, t] }, lengthAxis: 'x', widthAxis: 'y', materialSlot: 'drawer-box', grain: 'length', movable: 'slide' }),
      makePanel({ cabinetId: inst.id, localKey: key('p'), role: 'drawer-back', label: label('prednja'), box: { min: [x0 + t, fy, z1 - t], size: [w - 2 * t, fh, t] }, lengthAxis: 'x', widthAxis: 'y', materialSlot: 'drawer-box', grain: 'length', movable: 'slide' }),
    );
    if (set.dividerThickness && k < set.count) {
      // Fixed panel under this drawer's slot, full width and depth of the opening.
      parts.push(
        makePanel({
          cabinetId: inst.id,
          localKey: `${set.id}-p${k}`,
          role: 'shelf',
          label: `${set.label}: pregrada ${k}`,
          box: { min: [r.x0, slot.y0 - set.dividerThickness, r.z0], size: [r.x1 - r.x0, set.dividerThickness, r.z1 - r.z0] },
          lengthAxis: 'x',
          widthAxis: 'z',
          materialSlot: 'carcass',
          grain: 'length',
        }),
      );
    }
    hardware.push({ id: `${inst.id}/${key('klizaci')}`, cabinetId: inst.id, kind: 'slide', label: 'Klizači (par, generički)', quantity: 1, generic: true, note: `Ladica ${k}: dubina kutije ${(d / 1000).toFixed(0)} mm, bočni prostor ${set.slideClearance / 1000} mm.` });
  }
  return { parts, hardware, issues };
}

/** Which drawer set (and drawer number) a part local key belongs to. */
export function drawerOfPart(inst: AssemblyInstance, localKey: string): { set: DrawerSet; index: number | null } | null {
  for (const set of inst.drawerSets ?? []) {
    const m = new RegExp(`^${set.id}-(?:(\\d+)-|p\\d+$)`).exec(localKey);
    if (m) return { set, index: m[1] ? Number(m[1]) : null };
  }
  return null;
}

/** The shelf set that fills exactly this opening (drawers there replace it or turn it into dividers). */
export function shelvesInOpening(inst: AssemblyInstance, ref: { left: string | null; right: string | null; bottom: string | null; top: string | null }): ShelfSet | null {
  return (inst.shelfSets ?? []).find((s) => s.leftKey === ref.left && s.rightKey === ref.right && ((s.bottomKey === ref.bottom && s.topKey === ref.top) || (s.bottomKey === ref.top && s.topKey === ref.bottom))) ?? null;
}

/**
 * Drawers into an opening that holds shelves. `replace`: the shelves go, `count` drawers come.
 * `shelvesAsDividers`: the shelves become fixed boards between the drawers — one drawer per gap,
 * same positions as the shelves had (shelves are evenly spaced, like drawer slots with dividers).
 */
export function drawersFromShelves(inst: AssemblyInstance, shelves: ShelfSet, mode: 'replace' | 'shelvesAsDividers', count: number, withDividers: boolean): DrawerSet {
  const ref = { left: shelves.leftKey, right: shelves.rightKey, bottom: shelves.bottomKey, top: shelves.topKey };
  inst.shelfSets = (inst.shelfSets ?? []).filter((s) => s.id !== shelves.id);
  const base = makeDrawerSet(inst, ref, mode === 'replace' ? count : shelves.count + 1);
  const thickness = mode === 'replace' ? (withDividers ? mm(18) : undefined) : shelves.thickness;
  const set: DrawerSet = { ...base, ...(thickness && base.count > 1 ? { dividerThickness: thickness } : {}) };
  inst.drawerSets = [...(inst.drawerSets ?? []), set];
  return set;
}
